/**
 * Core: Canvas imports
 * -----------------------------------------------------------------------------
 * Import async components, global components, and Vuex stores for use in
 * CanvasApp Vue instance.
 *
 */
/* eslint-disable max-len, no-inline-comments, quote-props */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'

/**
 * Import async components.
 * - Must be manually updated with new async components (components/async/).
 * - Previously used a dynamic import statement which caused infinite build
 *   issue and generated unused files.
 * - Only parent components should be imported.
 * - Do not import components that are only ever used as a child component.
 * - Do not import async sub-components.
 * @returns {Object}
 */
export function getAsyncComponents() {
  return {
    // canvas-async-object-start
    'accordion-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.accordion-section' */'~async/accordion-section/accordion-section') }),
    'accordion-snippet': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.accordion-snippet' */'~async/accordion-snippet/accordion-snippet') }),
    'breadcrumbs': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.breadcrumbs' */'~async/breadcrumbs/breadcrumbs') }),
    'cart-drawer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.cart-drawer' */'~async/cart-drawer/cart-drawer') }),
    'collection-doorways': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.collection-doorways' */'~async/collection-doorways/collection-doorways') }),
    'collection-hero': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.collection-hero' */'~async/collection-hero/collection-hero') }),
    'customers-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-account' */'~async/customers/account/customers-account') }),
    'customers-activate-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-activate-account' */'~async/customers/activate-account/customers-activate-account') }),
    'customers-addresses': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-addresses' */'~async/customers/addresses/customers-addresses') }),
    'customers-login': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-login' */'~async/customers/login/customers-login') }),
    'customers-order': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-order' */'~async/customers/order/customers-order') }),
    'customers-orders': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-orders' */'~async/customers/orders/customers-orders') }),
    'customers-register': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-register' */'~async/customers/register/customers-register') }),
    'customers-reset-password': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-reset-password' */'~async/customers/reset-password/customers-reset-password') }),
    'featured-collection': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.featured-collection' */'~async/featured-collection/featured-collection') }),
    'heading-copy': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.heading-copy' */'~async/heading-copy/heading-copy') }),
    'hero-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.hero-banner' */'~async/hero-banner/hero-banner') }),
    'hotspot-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.hotspot-section' */'~async/hotspot-section/hotspot-section') }),
    'in-grid-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.in-grid-banner' */'~async/in-grid-banner/in-grid-banner') }),
    'main-404': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-404' */'~async/main-404/main-404') }),
    'main-article': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-article' */'~async/main-article/main-article') }),
    'main-blog': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-blog' */'~async/main-blog/main-blog') }),
    'main-cart': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-cart' */'~async/main-cart/main-cart') }),
    'main-collection': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-collection' */'~async/main-collection/main-collection') }),
    'main-list-collections': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-list-collections' */'~async/main-list-collections/main-list-collections') }),
    'main-page': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-page' */'~async/main-page/main-page') }),
    'main-product': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-product' */'~async/main-product/main-product') }),
    'main-search': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-search' */'~async/main-search/main-search') }),
    'multi-column': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.multi-column' */'~async/multi-column/multi-column') }),
    'multi-video': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.multi-video' */'~async/multi-video/multi-video') }),
    'newsletter-sign-up': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.newsletter-sign-up' */'~async/site-footer-group/newsletter-sign-up/newsletter-sign-up') }),
    'predictive-search': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.predictive-search' */'~async/predictive-search/predictive-search') }),
    'product-recommendations': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-recommendations' */'~async/product-recommendations/product-recommendations') }),
    'promo-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.promo-banner' */'~async/promo-banner/promo-banner') }),
    'site-footer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.site-footer' */'~async/site-footer-group/site-footer/site-footer') }),
    'text-and-image': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.text-and-image' */'~async/text-and-image/text-and-image') }),
    'transition-height': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.transition-height' */'~async/transition-height/transition-height') }),
    'trustpilot-placement': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.trustpilot-placement' */'~async/trustpilot-placement/trustpilot-placement') }),
    'wishlist-button': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.wishlist-button' */'~async/wishlist-button/wishlist-button') }),
    // canvas-async-object-end
  }
}

/**
 * Import global components.
 */
// canvas-global-import-start
import Accessibility from '~global/core/accessibility'
import ArtDirection from '~global/images/art-direction'
import Btn from '~global/btn/btn'
import CollectorsClubSettings from '~global/collectors-club-settings/collectors-club-settings'
import Index from '~global/core/index'
import Navigation from '~global/site-header-group/navigation/navigation'
import Overlay from '~global/overlay/overlay'
import ProductCard from '~global/product-card/product-card'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import SearchBar from '~global/search-bar/search-bar'
import SiteHeader from '~global/site-header-group/site-header/site-header'
import SiteHeaderGroup from '~global/site-header-group/site-header-group'
// canvas-global-import-end

/**
 * Get global components and imports.
 * - Must be manually updated with new global components (components/global).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 * @returns {Object}
 */
export function getGlobalComponents() {
  return {
    // canvas-global-object-start
    'accessibility': Accessibility,
    'art-direction': ArtDirection,
    'btn': Btn,
    'collectors-club-settings': CollectorsClubSettings,
    'index': Index,
    'navigation': Navigation,
    'overlay': Overlay,
    'product-card': ProductCard,
    'product-prices': ProductPrices,
    'responsive-image': ResponsiveImage,
    'search-bar': SearchBar,
    'site-header': SiteHeader,
    'site-header-group': SiteHeaderGroup,
    // canvas-global-object-end
  }
}

/**
 * Import web components.
 * - Must be manually updated with new async web components (components/async).
 * - Use component command import to update.
 */
// canvas-web-import-start
import(/* webpackChunkName: 'web.collectors-customer-hero' */'~web/collectors-customer-hero/collectors-customer-hero')
import(/* webpackChunkName: 'web.factory-details' */'~web/factory-details/factory-details')
import(/* webpackChunkName: 'web.faq-page' */'~web/faq-page/faq-page')
import(/* webpackChunkName: 'web.locked-collectors-content' */'~web/locked-collectors-content/locked-collectors-content')
import(/* webpackChunkName: 'web.product-trustpilot-section' */'~web/product-trustpilot-section/product-trustpilot-section')
import(/* webpackChunkName: 'web.request-catalogue' */'~web/request-catalogue/request-catalogue')
import(/* webpackChunkName: 'web.social-sharing-section' */'~web/social-sharing-section/social-sharing-section')
// canvas-web-import-end

/**
 * Import Vuex stores.
 * - Import with Store suffix to avoid conflicts with global component imports.
 */
// canvas-stores-import-start
import AccessibilityStore from '~stores/accessibility'
import CartStore from '~stores/cart'
import CollectorsClubStore from '~stores/collectors-club'
import ComponentsStore from '~stores/components'
import IndexStore from '~stores/index'
import OverlaysStore from '~stores/overlays'
// canvas-stores-import-end

/**
 * Get Vuex stores and imports.
 * - Must be manually updated with new Vuex stores (stores/).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 */
export function getVuexStores() {
  return {
    // canvas-stores-object-start
    'accessibility': AccessibilityStore,
    'cart': CartStore,
    'collectorsClub': CollectorsClubStore,
    'components': ComponentsStore,
    'index': IndexStore,
    'overlays': OverlaysStore,
    // canvas-stores-object-end
  }
}
